/* eslint-disable @typescript-eslint/no-invalid-void-type */
import VehicleStatus from '../../core/enums/VehicleStatus';
import Vehicle from '../../core/models/Vehicle';
import VehicleAuctionHistory from '../../core/models/VehicleAuctionHistory';
import { apiSlice } from './apiSlice';

export const vehicleApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVehicles: builder.query<Vehicle[], void>({
      query: () => '/vehicles',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Vehicle' as const, id })),
              { type: 'Vehicle', id: 'LIST' },
            ]
          : [{ type: 'Vehicle', id: 'LIST' }],
    }),
    getAuctionReadyVehicles: builder.query<Vehicle[], void>({
      query: () => '/vehicles/auctionReady',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Vehicle' as const, id })),
              { type: 'Vehicle', id: 'LIST' },
            ]
          : [{ type: 'Vehicle', id: 'LIST' }],
    }),
    getVehicle: builder.query<Vehicle, number>({
      query: (id) => `/vehicles/${id}`,
      providesTags: (result, error, id) => [{ type: 'Vehicle', id }],
    }),
    getVehicleAuctionHistory: builder.query<VehicleAuctionHistory[], string>({
      query: (id) => `/vehicles/auctions/${id}`,
      providesTags: (result, error, id) => [{ type: 'VehicleAuctionHistory', id }],
    }),
    addVehicle: builder.mutation<Vehicle, Vehicle>({
      query: (data) => ({
        url: '/vehicles',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Vehicle', id: 'LIST' }],
    }),
    updateVehicle: builder.mutation<Vehicle, Vehicle>({
      query: (data) => ({
        url: '/vehicles',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Vehicle', id }],
    }),
    registerVehicle: builder.mutation<Vehicle, string>({
      query: (id) => ({
        url: '/vehicles/updateStatus',
        method: 'PUT',
        body: { id: id, status: 2 },
      }),
      invalidatesTags: ['Vehicle'],
    }),
    setPrice: builder.mutation<
      Vehicle,
      { id: string; status: number; startingPrice: number; reservePrice: number }
    >({
      query: ({ id, status, startingPrice, reservePrice }) => ({
        url: '/vehicles/updateStatus',
        method: 'PUT',
        body: { id: id, status: status, startingPrice: startingPrice, reservePrice: reservePrice },
      }),
      invalidatesTags: ['Vehicle'],
    }),
    updateVehicleStatus: builder.mutation<Vehicle, { id: string; status: VehicleStatus }>({
      query: (args) => ({
        url: '/vehicles/updateStatus',
        method: 'PUT',
        body: { id: args.id, status: args.status },
      }),
      invalidatesTags: ['Vehicle'],
    }),
    deleteImage: builder.mutation<void, { vehicleId: string; imageId: string }>({
      query: (args) => ({
        url: `/vehicles/${args.vehicleId}/images/${args.imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vehicle'],
    }),
    sortImages: builder.mutation<void, Vehicle>({
      query: (data) => ({
        url: `/vehicles/images/sortOrder/${data.id}`,
        method: 'PUT',
        body: data.images.map((image, index) => ({
          id: image.id,
          filePath: image.filePath,
          fileName: image.fileName,
          sortOrder: index,
        })),
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Vehicle', id }],
    }),
  }),
});

export const {
  useGetVehiclesQuery,
  useGetAuctionReadyVehiclesQuery,
  useGetVehicleQuery,
  useGetVehicleAuctionHistoryQuery,
  useAddVehicleMutation,
  useUpdateVehicleMutation,
  useRegisterVehicleMutation,
  useSetPriceMutation,
  useUpdateVehicleStatusMutation,
  useDeleteImageMutation,
  useSortImagesMutation,
} = vehicleApiSlice;
